import { forwardRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Box, Image } from '@packages/shared';

import type { NavigationItem } from '../navigationTypes';
import { ResponsiveFlyoutItem } from './ResponsiveFlyoutItem';

const messages = defineMessages({
  currentCategoryLink: {
    id: 'header.navigation.flyout.currentCategoryLink',
    defaultMessage: 'Alles in {categoryName}',
  },
  moreCategories: {
    id: 'header.navigation.flyout.moreCategories',
    defaultMessage: 'Mehr ...',
  },
});

type ResponsiveFlyoutContentProps = {
  item?: NavigationItem;
  onItemClick?: (item: NavigationItem) => void;
  onNavigate?: (item: NavigationItem) => void;
};

/**
 * Content for the navigation popup, displaying child items of the currently selected category.
 */
export const ResponsiveFlyoutContent = forwardRef<HTMLElement, ResponsiveFlyoutContentProps>(
  ({ item, onItemClick, onNavigate }, ref) => {
    const hasSelfLink = item?.uri !== undefined;

    const tooManyItems = (item?.children?.length ?? 0) > (hasSelfLink ? 24 : 25);

    const maxItems = tooManyItems && hasSelfLink ? 23 : 25;

    return (
      <Box>
        {item?.image && (
          <Box
            sx={{
              display: { xs: item?.level === 1 ? 'block' : 'none', lg: 'none' },
              alignSelf: 'stretch',
              overflow: 'hidden',
              position: 'relative',
              height: '8rem',
              backgroundColor: 'grey.light',
            }}
          >
            <Image
              src={item.image}
              // decorative image in line with WAI standards
              alt=""
              fill
              style={{ objectFit: 'contain', width: '100%', height: '100%' }}
            />
          </Box>
        )}
        <Box
          ref={ref}
          component="ul"
          alignItems="stretch"
          sx={(theme) => ({
            listStyle: 'none',
            padding: 0,
            margin: 0,
            alignItems: 'stretch',
            [theme.breakpoints.up('lg')]: {
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gridTemplateRows: 'repeat(5, auto)',
              gap: 1.5,
              gridAutoFlow: 'column',
            },
            [theme.breakpoints.down('lg')]: {
              display: 'flex',
              flexDirection: 'column',
            },
          })}
        >
          {hasSelfLink && (
            <ResponsiveFlyoutItem
              key={item.uri}
              href={item.uri}
              allowNavigation
              onClick={() => onItemClick?.(item)}
              onNavigate={() => onNavigate?.(item)}
            >
              <FormattedMessage
                {...messages.currentCategoryLink}
                values={{
                  categoryName: item.displayName,
                }}
              />
            </ResponsiveFlyoutItem>
          )}
          {item?.children?.slice(0, maxItems)?.map((child) => (
            <ResponsiveFlyoutItem
              key={child.uri}
              href={child.uri}
              allowNavigation={!!child.uri && !child.children.length}
              onClick={() => onItemClick?.(child)}
              onNavigate={() => onNavigate?.(child)}
            >
              {child.displayName}
            </ResponsiveFlyoutItem>
          ))}
          {tooManyItems && hasSelfLink && (
            <ResponsiveFlyoutItem
              key="more"
              href={item.uri}
              allowNavigation
              onClick={() => onItemClick?.(item)}
              onNavigate={() => onNavigate?.(item)}
            >
              <FormattedMessage {...messages.moreCategories} />
            </ResponsiveFlyoutItem>
          )}
        </Box>
      </Box>
    );
  },
);
